import { useEffect, useState } from "react";
import api, { formatPrice, formatApiErrorDetail, formatDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import useUserSocket from "../lib/useUserSocket";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Handshake, TrendingDown, CheckCircle2, XCircle, Clock, Send, History, CreditCard, Zap } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
    pending: { label: "Wartet auf Antwort", className: "bg-amber-100 text-amber-800", icon: Clock },
    countered: { label: "Gegenvorschlag", className: "bg-blue-100 text-blue-800", icon: TrendingDown },
    accepted: { label: "Angenommen", className: "bg-green-100 text-green-800", icon: CheckCircle2 },
    rejected: { label: "Abgelehnt", className: "bg-red-100 text-red-800", icon: XCircle },
    withdrawn: { label: "Zurückgezogen", className: "bg-gray-100 text-gray-700", icon: XCircle },
};

export function OfferStatusBadge({ status }) {
    const meta = STATUS_META[status] || STATUS_META.pending;
    const Icon = meta.icon;
    return (
        <Badge className={`${meta.className} hover:${meta.className} uppercase tracking-wide font-bold rounded-sm gap-1`} data-testid="offer-status">
            <Icon className="w-3 h-3" /> {meta.label}
        </Badge>
    );
}

export default function OfferPanel({ listing, onUpdate }) {
    const { user, setSession } = useAuth();
    const [myOffer, setMyOffer] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [amount, setAmount] = useState("");
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    // Gast-Preisvorschlag (ohne Login): Kontaktdaten + Hinweis bei bestehendem Konto
    const [guestFirst, setGuestFirst] = useState("");
    const [guestLast, setGuestLast] = useState("");
    const [guestEmail, setGuestEmail] = useState("");
    const [guestPhone, setGuestPhone] = useState("");
    const [existingAccount, setExistingAccount] = useState(false);

    const isLoggedIn = user && user !== false;
    const isOwner = isLoggedIn && user.id === listing.owner_id;
    const sellerAllows = listing.owner?.allow_offers;
    const listingAllows = listing.allow_offers;
    const available = listingAllows && sellerAllows && !listing.sold && listing.sale_type !== "auction";

    useEffect(() => {
        if (!isLoggedIn || isOwner || !available) {
            setMyOffer(null);
            return;
        }
        api.get(`/listings/${listing.id}/offers/mine`)
            .then((r) => setMyOffer(r.data))
            .catch(() => setMyOffer(null));
    }, [listing.id, isLoggedIn, isOwner, available]);

    // Live updates: re-fetch the current user's offer for this listing when an offer event fires
    useUserSocket(isLoggedIn && !isOwner && available ? user.id : null, (evt) => {
        if (!evt || evt.listing_id !== listing.id) return;
        api.get(`/listings/${listing.id}/offers/mine`)
            .then((r) => setMyOffer(r.data))
            .catch(() => { /* noop */ });
    });

    if (!available) return null;
    if (isOwner) return null;

    const submitOffer = async () => {
        setError("");
        const value = Number(amount);
        if (!value || value <= 0) {
            setError("Bitte einen gültigen Betrag eingeben");
            return;
        }
        if (value >= listing.price) {
            setError(`Bitte unter dem Festpreis ${formatPrice(listing.price)} bleiben`);
            return;
        }
        const wasGuest = !isLoggedIn;
        const guestEmailUsed = guestEmail.trim();
        // Gast-Flow: Kontaktdaten erfassen + passwortloses Konto anlegen / auto-login,
        // bevor der Vorschlag gesendet wird (höhere Conversion, keine Login-Hürde).
        if (!isLoggedIn) {
            const first = guestFirst.trim();
            const last = guestLast.trim();
            const name = `${first} ${last}`.trim();
            const email = guestEmail.trim();
            const phone = guestPhone.trim();
            if (first.length < 2) {
                setError("Bitte Ihren Vornamen eingeben");
                return;
            }
            if (last.length < 2) {
                setError("Bitte Ihren Nachnamen eingeben");
                return;
            }
            if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
                setError("Bitte eine gültige E-Mail-Adresse eingeben");
                return;
            }
            if (phone.replace(/[^0-9]/g, "").length < 6) {
                setError("Bitte eine gültige Handynummer eingeben");
                return;
            }
            setSubmitting(true);
            try {
                const { data } = await api.post("/auth/guest", {
                    name,
                    email,
                    phone: phone || null,
                });
                if (data.existing) {
                    setSubmitting(false);
                    setExistingAccount(true);
                    setError("Für diese E-Mail existiert bereits ein Konto. Bitte einloggen für vollen Zugriff.");
                    return;
                }
                setSession(data.user, data.token);
            } catch (e) {
                setSubmitting(false);
                setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                return;
            }
        } else {
            setSubmitting(true);
        }
        try {
            const { data } = await api.post(`/listings/${listing.id}/offers`, {
                amount: value,
                message: message.trim() || null,
            });
            setMyOffer(data);
            setDialogOpen(false);
            setAmount("");
            setMessage("");
            setGuestFirst("");
            setGuestLast("");
            setGuestEmail("");
            setGuestPhone("");
            toast.success(`Preisvorschlag ${formatPrice(value)} gesendet`);
            if (wasGuest) {
                const { promptSecureAccount } = await import("../lib/secureAccount");
                promptSecureAccount(guestEmailUsed);
            }
            // Best-effort referral attribution — credits the originating
            // Vermittler with points when the buyer came via /v/{code}
            try {
                const { attributeReferralIfPresent } = await import("../lib/referral");
                if (data?.id) await attributeReferralIfPresent(api, { reference_id: data.id, reference_type: "offer" });
            } catch { /* silent */ }
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const respondToCounter = async (action) => {
        try {
            const { data } = await api.post(`/offers/${myOffer.id}/${action}`);
            setMyOffer(data);
            toast.success(action === "accept" ? "Gegenvorschlag angenommen" : "Gegenvorschlag abgelehnt");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Aktion fehlgeschlagen");
        }
    };

    const withdraw = async () => {
        try {
            const { data } = await api.post(`/offers/${myOffer.id}/withdraw`);
            setMyOffer(data);
            toast("Vorschlag zurückgezogen");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Aktion fehlgeschlagen");
        }
    };

    const hasOpenOffer = myOffer && ["pending", "countered"].includes(myOffer.status);
    const finished = myOffer && ["accepted", "rejected", "withdrawn"].includes(myOffer.status);

    return (
        <div className="bg-white border-2 border-[#FFCC00] rounded-sm p-5" data-testid="offer-panel">
            <div className="flex items-center gap-2 mb-3">
                <div className="bg-[#FFCC00] text-[#111827] px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                    <Handshake className="w-3 h-3" /> Preisvorschlag möglich
                </div>
            </div>

            {!myOffer && (
                <>
                    <p className="text-sm text-gray-600 mb-3">
                        Der Verkäufer akzeptiert Preisvorschläge unter dem Festpreis. Senden Sie Ihr bestes Angebot.
                        {!isLoggedIn && (
                            <span className="block mt-1 text-[#92400E] font-bold">Kein Login nötig — einfach Kontaktdaten angeben.</span>
                        )}
                    </p>
                    <Button
                        onClick={() => setDialogOpen(true)}
                        className="w-full bg-[#111827] hover:bg-black text-white font-bold rounded-sm h-11"
                        data-testid="open-offer-dialog"
                    >
                        <TrendingDown className="w-4 h-4 mr-2" /> Preisvorschlag senden
                    </Button>
                </>
            )}

            {hasOpenOffer && (
                <div className="space-y-3" data-testid="offer-status-panel">
                    <div className="flex items-center justify-between gap-2">
                        <OfferStatusBadge status={myOffer.status} />
                        <span className="text-xs text-gray-500">{formatDate(myOffer.updated_at)}</span>
                    </div>
                    {myOffer.status === "pending" && (
                        <>
                            <div className="text-sm">
                                Ihr Vorschlag: <strong className="font-mono-num">{formatPrice(myOffer.amount)}</strong>
                            </div>
                            <Button variant="outline" onClick={withdraw} className="w-full rounded-sm border-[#111827] text-sm" data-testid="withdraw-offer-btn">
                                Vorschlag zurückziehen
                            </Button>
                        </>
                    )}
                    {myOffer.status === "countered" && (
                        <>
                            <div className={`rounded-sm p-3 border ${myOffer.auto_countered ? "bg-[#FFFBEB] border-[#FDE68A]" : "bg-blue-50 border-blue-200"}`} data-testid="counter-offer-box">
                                <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5 ${myOffer.auto_countered ? "text-[#B45309]" : "text-blue-700"}`}>
                                    {myOffer.auto_countered ? (
                                        <>
                                            <Zap className="w-3.5 h-3.5" />
                                            Automatischer Gegenvorschlag des Händlers
                                        </>
                                    ) : "Gegenvorschlag des Verkäufers"}
                                </div>
                                <div className="font-display font-black text-2xl text-[#111827] font-mono-num">{formatPrice(myOffer.counter_amount)}</div>
                                {myOffer.auto_countered && (
                                    <p className="text-xs text-[#92400E] mt-1 font-bold" data-testid="auto-counter-hint">
                                        Der Händler nimmt diesen Preis sofort an — jetzt mit einem Klick zuschlagen.
                                    </p>
                                )}
                                {myOffer.counter_message && (
                                    <p className="text-xs text-gray-600 mt-2 italic">„{myOffer.counter_message}"</p>
                                )}
                                <div className="text-xs text-gray-500 mt-2">
                                    Ihr ursprünglicher Vorschlag: <span className="font-mono-num">{formatPrice(myOffer.amount)}</span>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <Button onClick={() => respondToCounter("accept")} className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-sm" data-testid="accept-counter-btn">
                                    <CheckCircle2 className="w-4 h-4 mr-1" /> {myOffer.auto_countered ? "Jetzt annehmen" : "Annehmen"}
                                </Button>
                                <Button onClick={() => respondToCounter("reject")} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50 font-bold rounded-sm" data-testid="reject-counter-btn">
                                    <XCircle className="w-4 h-4 mr-1" /> Ablehnen
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {finished && (
                <div className="space-y-2" data-testid="offer-finished">
                    <OfferStatusBadge status={myOffer.status} />
                    {myOffer.status === "accepted" && myOffer.final_amount && (
                        <div className="bg-green-50 border border-green-200 rounded-sm p-3">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-green-700">Zuschlag</div>
                            <div className="font-display font-black text-2xl text-[#111827] font-mono-num">{formatPrice(myOffer.final_amount)}</div>
                            {!myOffer.paid && (
                                <Button
                                    onClick={async () => {
                                        try {
                                            const { data } = await api.post(`/offers/${myOffer.id}/pay`, { origin_url: window.location.origin });
                                            window.location.href = data.url;
                                        } catch (e) {
                                            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Zahlung konnte nicht gestartet werden");
                                        }
                                    }}
                                    className="w-full mt-3 bg-[#111827] hover:bg-black text-white font-bold rounded-sm h-11 uppercase tracking-wider"
                                    data-testid="pay-offer-btn"
                                >
                                    <CreditCard className="w-4 h-4 mr-2" /> Jetzt sicher bezahlen
                                </Button>
                            )}
                            {myOffer.paid && (
                                <div className="mt-3 bg-green-600 text-white p-2 rounded-sm flex items-center gap-2 text-sm font-bold">
                                    <CheckCircle2 className="w-4 h-4" /> Bezahlt
                                </div>
                            )}
                            <p className="text-xs text-gray-600 mt-2">via Stripe · Treuhand-Zahlung an Plattform</p>
                        </div>
                    )}
                    {(myOffer.status === "rejected" || myOffer.status === "withdrawn") && (
                        <Button onClick={() => { setMyOffer(null); setDialogOpen(true); }} variant="outline" className="w-full rounded-sm border-[#111827]" data-testid="resubmit-offer-btn">
                            Neuen Vorschlag senden
                        </Button>
                    )}
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="rounded-sm" data-testid="offer-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black text-2xl flex items-center gap-2">
                            <Handshake className="w-6 h-6 text-[#FFCC00]" /> Preisvorschlag senden
                        </DialogTitle>
                        <DialogDescription className="space-y-2 pt-2">
                            <span className="block">Für: <strong className="text-[#111827]">{listing.title}</strong></span>
                            <span className="block text-sm">Festpreis: <strong className="text-[#111827] font-mono-num">{formatPrice(listing.price)}</strong></span>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        {!isLoggedIn && (
                            <div className="space-y-3 pb-4 border-b border-gray-100" data-testid="guest-offer-fields">
                                <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-sm p-2.5 text-xs text-[#92400E]">
                                    Kein Konto nötig — wir legen automatisch eines für Sie an, damit Sie den Status Ihres Vorschlags verfolgen können.
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Vorname</label>
                                        <Input
                                            value={guestFirst}
                                            onChange={(e) => setGuestFirst(e.target.value)}
                                            placeholder="Max"
                                            className="rounded-sm h-11"
                                            data-testid="guest-first-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Nachname</label>
                                        <Input
                                            value={guestLast}
                                            onChange={(e) => setGuestLast(e.target.value)}
                                            placeholder="Mustermann"
                                            className="rounded-sm h-11"
                                            data-testid="guest-last-input"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">E-Mail</label>
                                    <Input
                                        type="email"
                                        value={guestEmail}
                                        onChange={(e) => { setGuestEmail(e.target.value); setExistingAccount(false); }}
                                        placeholder="ihre@email.de"
                                        className="rounded-sm h-11"
                                        data-testid="guest-email-input"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Handynummer</label>
                                    <Input
                                        type="tel"
                                        value={guestPhone}
                                        onChange={(e) => setGuestPhone(e.target.value)}
                                        placeholder="z.B. 0151 23456789"
                                        className="rounded-sm h-11"
                                        data-testid="guest-phone-input"
                                    />
                                </div>
                                {existingAccount && (
                                    <a
                                        href={`/login?email=${encodeURIComponent(guestEmail.trim())}`}
                                        className="inline-flex items-center justify-center w-full h-10 rounded-sm border border-[#111827] text-sm font-bold text-[#111827] hover:bg-gray-50"
                                        data-testid="guest-existing-login-link"
                                    >
                                        Jetzt einloggen
                                    </a>
                                )}
                            </div>
                        )}
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Ihr Vorschlag (€)</label>
                            <Input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder={`unter ${listing.price} €`}
                                className="rounded-sm h-12 font-mono-num font-bold text-lg"
                                data-testid="offer-amount-input"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Nachricht (optional)</label>
                            <Textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="z.B. Barzahlung, schnelle Abholung, ..."
                                className="rounded-sm min-h-[80px]"
                                maxLength={500}
                                data-testid="offer-message-input"
                            />
                        </div>
                        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm" data-testid="offer-error">{error}</div>}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Abbrechen</Button>
                        <Button
                            onClick={submitOffer}
                            disabled={submitting}
                            className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider"
                            data-testid="offer-submit-btn"
                        >
                            <Send className="w-4 h-4 mr-1" /> {isLoggedIn ? "Vorschlag senden" : "Konto erstellen & senden"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
